// Profile.js
import React, { useState, useEffect } from "react";
import { Button } from "./ui";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
} from "mdb-react-ui-kit";
import { api } from "../services";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";
import {
  translateProfileApiMessage,
} from "../utils/translateProfileApiMessage";
import { translateAuthApiMessage } from "../utils/translateAuthApiMessage";
import NativeInput from "./NativeInput";
import VerifyPhoneOtp from "./VerifyPhoneOtp";
import { toast } from "react-toastify";
import {
  isValidEmail,
  isValidKsaPhone,
  normalizeKsaMobile,
  formatKsaE164,
} from "../utils/validators";
import "./Profile.css";

function profileJwtUserType(isAdminPath) {
  try {
    const token = localStorage.getItem(isAdminPath ? "adminToken" : "token");
    if (!token) return "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.user?.user_type || "";
  } catch {
    return "";
  }
}

const MIN_PASSWORD_LENGTH = 6;

function getEmailFieldError(value, t) {
  const trimmed = (value || "").trim();
  if (!trimmed) return t("profilePage.validation.emailRequired");
  if (!isValidEmail(trimmed)) return t("profilePage.validation.invalidEmail");
  return "";
}

function getPhoneFieldError(value, t) {
  const trimmed = (value || "").trim();
  if (!trimmed) return t("profilePage.validation.phoneRequired");
  if (!isValidKsaPhone(trimmed)) {
    return t("profilePage.validation.invalidKsaPhone");
  }
  return "";
}

function normalizeProfilePayload(userData) {
  const email = (userData.email || "").trim().toLowerCase();
  const mobile = normalizeKsaMobile(userData.phone);
  const phone = mobile ? formatKsaE164(mobile) : (userData.phone || "").trim();
  return {
    username: (userData.username || "").trim(),
    email,
    phone,
  };
}

function ProfileFormCard({ title, children }) {
  return (
    <MDBCol size="12" lg="6" className="profile-form-col">
      <MDBCard className="mb-0 profile-form-card h-100">
        <MDBCardHeader>
          <h4 className="mb-0">{title}</h4>
        </MDBCardHeader>
        <MDBCardBody className="profile-form-card-body">{children}</MDBCardBody>
      </MDBCard>
    </MDBCol>
  );
}

function FormStatusMessage({ message, isError }) {
  if (!message) return null;
  return (
    <p className={isError ? "text-danger profile-form-message" : "text-success profile-form-message"}>
      {message}
    </p>
  );
}

function Profile() {
  const { t } = useTranslation("translation");
  const [userData, setUserData] = useState({
    username: "",
    email: "",
    phone: "",
    emailVerified: false,
    phoneVerified: false,
  });
  const [showPhoneOtp, setShowPhoneOtp] = useState(false);
  const [resendVerifyLoading, setResendVerifyLoading] = useState(false);
  const phoneOtpEnabled = process.env.REACT_APP_ENABLE_PHONE_OTP === "true";
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [passwordMessageIsError, setPasswordMessageIsError] = useState(false);
  const [userMessage, setUserMessage] = useState("");
  const [userMessageIsError, setUserMessageIsError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    email: "",
    phone: "",
  });
  const [passwordFieldErrors, setPasswordFieldErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [submittingProfile, setSubmittingProfile] = useState(false);
  const [submittingPassword, setSubmittingPassword] = useState(false);

  const { pathname } = useLocation();

  const isAdminPath = pathname.startsWith("/admin");
  const tokenVal = localStorage.getItem(isAdminPath ? "adminToken" : "token");
  const userType = profileJwtUserType(isAdminPath);
  const isStaffUser = userType === "staff";
  const canEditProfile = !isStaffUser;

  const clearProfileMessages = () => {
    setUserMessage("");
    setUserMessageIsError(false);
  };

  const clearPasswordMessage = () => {
    setMessage("");
    setPasswordMessageIsError(false);
  };

  const applyApiFieldErrors = (apiErrors) => {
    if (!Array.isArray(apiErrors) || !apiErrors.length) return false;
    const next = { email: "", phone: "" };
    apiErrors.forEach((entry) => {
      const field = entry?.path || entry?.param;
      const msg = entry?.msg || entry?.message;
      if ((field === "email" || field === "phone") && msg) {
        next[field] = translateProfileApiMessage(msg, t);
      }
    });
    if (next.email || next.phone) {
      setFieldErrors(next);
      return true;
    }
    return false;
  };

  const validateField = (field, value) => {
    const error =
      field === "email"
        ? getEmailFieldError(value, t)
        : field === "phone"
        ? getPhoneFieldError(value, t)
        : "";

    setFieldErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateProfileFields = () => {
    const next = {
      email: getEmailFieldError(userData.email, t),
      phone: getPhoneFieldError(userData.phone, t),
    };

    setFieldErrors(next);
    return !next.email && !next.phone;
  };

  const validatePasswordField = (field, values = {}) => {
    const current = (values.currentPassword ?? oldPassword).trim();
    const newP = (values.newPassword ?? newPassword).trim();
    const confirm = (values.confirmPassword ?? confirmPassword).trim();
    let error = "";

    if (field === "currentPassword" && !current) {
      error = t("profileCurrentPasswordRequired");
    } else if (field === "newPassword") {
      if (!newP) {
        error = t("profileNewPasswordRequired");
      } else if (newP.length < MIN_PASSWORD_LENGTH) {
        error = t("profilePasswordMinLength");
      }
    } else if (field === "confirmPassword") {
      if (!confirm) {
        error = t("profileConfirmPasswordRequired");
      } else if (newP && confirm !== newP) {
        error = t("profilePasswordMismatch");
      }
    }

    setPasswordFieldErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validatePasswordFields = () => {
    const current = oldPassword.trim();
    const newP = newPassword.trim();
    const confirm = confirmPassword.trim();
    const next = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };

    if (!current) {
      next.currentPassword = t("profileCurrentPasswordRequired");
    }
    if (!newP) {
      next.newPassword = t("profileNewPasswordRequired");
    } else if (newP.length < MIN_PASSWORD_LENGTH) {
      next.newPassword = t("profilePasswordMinLength");
    }
    if (!confirm) {
      next.confirmPassword = t("profileConfirmPasswordRequired");
    } else if (newP && confirm !== newP) {
      next.confirmPassword = t("profilePasswordMismatch");
    }

    setPasswordFieldErrors(next);
    return (
      !next.currentPassword && !next.newPassword && !next.confirmPassword
    );
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get("/profile", {
          headers: {
            Authorization: `Bearer ${tokenVal}`,
          },
        });
        const data = response.data || {};
        setUserData({
          username: data.username || "",
          email: data.email || "",
          phone: data.phone || "",
          emailVerified: Boolean(data.emailVerified),
          phoneVerified: Boolean(data.phoneVerified),
        });
        setFieldErrors({ email: "", phone: "" });
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    };
    fetchProfile();
  }, [tokenVal]);

  const handleUserDetailsUpdate = async () => {
    if (!canEditProfile) return;

    clearProfileMessages();
    if (!validateProfileFields()) {
      return;
    }

    const payload = normalizeProfilePayload(userData);

    setSubmittingProfile(true);
    try {
      const response = await api.put("/profile", payload, {
        headers: {
          Authorization: `Bearer ${tokenVal}`,
        },
      });
      setUserMessage(
        translateProfileApiMessage(
          response.data.message,
          t
        ) || t("profileUpdateSuccess")
      );
      setUserMessageIsError(false);
      setFieldErrors({ email: "", phone: "" });
    } catch (err) {
      console.error("Error updating profile:", err);
      const hasFieldErrors = applyApiFieldErrors(err?.response?.data?.errors);
      if (!hasFieldErrors) {
        const raw = getApiErrorMessage(err);
        const apiMessage = translateProfileApiMessage(raw, t);
        if (/email/i.test(raw)) {
          setFieldErrors((prev) => ({ ...prev, email: apiMessage }));
        } else if (/phone/i.test(raw)) {
          setFieldErrors((prev) => ({ ...prev, phone: apiMessage }));
        } else if (apiMessage) {
          setUserMessage(apiMessage);
          setUserMessageIsError(true);
        } else {
          setUserMessage(t("profileUpdateError"));
          setUserMessageIsError(true);
        }
      }
    } finally {
      setSubmittingProfile(false);
    }
  };

  const handleProfileFieldChange = (field, value) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    }
    if (userMessage) {
      clearProfileMessages();
    }
  };

  const handlePasswordFieldChange = (field, value) => {
    if (field === "currentPassword") setOldPassword(value);
    if (field === "newPassword") setNewPassword(value);
    if (field === "confirmPassword") setConfirmPassword(value);

    if (passwordFieldErrors[field]) {
      setPasswordFieldErrors((prev) => ({ ...prev, [field]: "" }));
    }
    if (message) {
      clearPasswordMessage();
    }
  };

  const handlePasswordUpdate = async () => {
    clearPasswordMessage();
    if (!validatePasswordFields()) {
      return;
    }

    setSubmittingPassword(true);
    try {
      const response = await api.put(
        "/update-password",
        {
          currentPassword: oldPassword,
          newPassword: newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${tokenVal}`,
          },
        }
      );
      setMessage(
        translateProfileApiMessage(
          response.data.message,
          t
        ) || t("profilePasswordUpdateSuccess")
      );
      setPasswordMessageIsError(false);
      setPasswordFieldErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Error updating password:", err);
      const raw = getApiErrorMessage(err);
      const apiMessage = translateProfileApiMessage(raw, t);
      if (/incorrect current password/i.test(raw)) {
        setPasswordFieldErrors((prev) => ({
          ...prev,
          currentPassword: apiMessage,
        }));
      } else {
        setMessage(apiMessage || t("profileUpdateError"));
        setPasswordMessageIsError(true);
      }
    } finally {
      setSubmittingPassword(false);
    }
  };

  return (
    <MDBContainer fluid className="p-4 content-area profile-page-root">
      <h2>{t("Profile")}</h2>

      {showPhoneOtp && phoneOtpEnabled ? (
        <VerifyPhoneOtp
          phone={userData.phone}
          onBack={() => setShowPhoneOtp(false)}
          onVerified={() => {
            setUserData((prev) => ({ ...prev, phoneVerified: true }));
            setShowPhoneOtp(false);
          }}
        />
      ) : (
      <MDBRow className="g-4 align-items-stretch">
        <ProfileFormCard title={t("User Details")}>
          {isStaffUser && (
            <p className="text-muted small profile-form-hint">
              {t("profileStaffReadOnlyHint")}
            </p>
          )}
          <NativeInput
            label={t("Username")}
            type="text"
            value={userData.username}
            disabled={!canEditProfile}
            readOnly={!canEditProfile}
            onChange={(e) =>
              handleProfileFieldChange("username", e.target.value)
            }
          />
          <NativeInput
            label={t("email")}
            required={canEditProfile}
            type="email"
            value={userData.email}
            disabled={!canEditProfile}
            readOnly={!canEditProfile}
            invalid={!!fieldErrors.email}
            feedback={fieldErrors.email}
            onChange={(e) =>
              handleProfileFieldChange("email", e.target.value)
            }
            onBlur={(e) => validateField("email", e.target.value)}
          />
          <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
            <span
              className={`badge ${userData.emailVerified ? "bg-success" : "bg-warning text-dark"}`}
            >
              {userData.emailVerified
                ? t("profilePage.emailVerified", { defaultValue: "Email verified" })
                : t("profilePage.emailPending", { defaultValue: "Email pending verification" })}
            </span>
            {!userData.emailVerified && userData.email && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                disabled={resendVerifyLoading}
                onClick={async () => {
                  setResendVerifyLoading(true);
                  try {
                    await api.post("/resend-verification", { email: userData.email });
                    toast.success(
                      t("profilePage.verificationResent", {
                        defaultValue: "Verification email sent if the account needs it.",
                      })
                    );
                  } catch (err) {
                    toast.error(
                      translateAuthApiMessage(err.response?.data?.message, t) ||
                        t("profilePage.verificationResendFailed", {
                          defaultValue: "Failed to resend verification email.",
                        })
                    );
                  } finally {
                    setResendVerifyLoading(false);
                  }
                }}
              >
                {t("profilePage.resendVerification", { defaultValue: "Resend verification email" })}
              </button>
            )}
          </div>
          <NativeInput
            label={t("phone")}
            required={canEditProfile}
            type="tel"
            value={userData.phone}
            disabled={!canEditProfile}
            readOnly={!canEditProfile}
            invalid={!!fieldErrors.phone}
            feedback={fieldErrors.phone}
            placeholder={t("profilePhonePlaceholder")}
            onChange={(e) =>
              handleProfileFieldChange("phone", e.target.value)
            }
            onBlur={(e) => validateField("phone", e.target.value)}
          />
          <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
            <span
              className={`badge ${userData.phoneVerified ? "bg-success" : "bg-warning text-dark"}`}
            >
              {userData.phoneVerified
                ? t("profilePage.phoneVerified", { defaultValue: "Phone verified" })
                : t("profilePage.phonePending", { defaultValue: "Phone pending verification" })}
            </span>
            {phoneOtpEnabled && !userData.phoneVerified && canEditProfile && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0"
                onClick={() => setShowPhoneOtp(true)}
              >
                {t("profilePage.verifyPhone", { defaultValue: "Verify phone" })}
              </button>
            )}
          </div>
          <FormStatusMessage
            message={userMessage}
            isError={userMessageIsError}
          />
          {canEditProfile ? (
            <Button
              color="primary"
              onClick={handleUserDetailsUpdate}
              disabled={submittingProfile}
              className="profile-form-submit"
            >
              {submittingProfile ? t("saving") : t("Update Profile")}
            </Button>
          ) : (
            <div className="profile-form-submit-spacer" aria-hidden="true" />
          )}
        </ProfileFormCard>

        <ProfileFormCard title={t("Update Password")}>
          {isStaffUser && (
            <div
              className="profile-form-hint profile-form-hint--placeholder"
              aria-hidden="true"
            >
              &nbsp;
            </div>
          )}
          <NativeInput
            label={t("Current Password")}
            required
            type="password"
            value={oldPassword}
            invalid={!!passwordFieldErrors.currentPassword}
            feedback={passwordFieldErrors.currentPassword}
            onChange={(e) =>
              handlePasswordFieldChange("currentPassword", e.target.value)
            }
            onBlur={() => validatePasswordField("currentPassword")}
          />
          <NativeInput
            label={t("New Password")}
            required
            type="password"
            value={newPassword}
            invalid={!!passwordFieldErrors.newPassword}
            feedback={passwordFieldErrors.newPassword}
            onChange={(e) =>
              handlePasswordFieldChange("newPassword", e.target.value)
            }
            onBlur={() =>
              validatePasswordField("newPassword", {
                newPassword,
                confirmPassword,
              })
            }
          />
          <NativeInput
            label={t("profileConfirmPasswordLabel")}
            required
            type="password"
            value={confirmPassword}
            invalid={!!passwordFieldErrors.confirmPassword}
            feedback={passwordFieldErrors.confirmPassword}
            onChange={(e) =>
              handlePasswordFieldChange("confirmPassword", e.target.value)
            }
            onBlur={() =>
              validatePasswordField("confirmPassword", {
                newPassword,
                confirmPassword,
              })
            }
          />
          <FormStatusMessage message={message} isError={passwordMessageIsError} />
          <Button
            color="primary"
            onClick={handlePasswordUpdate}
            disabled={submittingPassword}
            className="profile-form-submit"
          >
            {submittingPassword ? t("saving") : t("Update Password")}
          </Button>
        </ProfileFormCard>
      </MDBRow>
      )}
    </MDBContainer>
  );
}

export default Profile;
